import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import serviceAreaContentService from '../../../services/serviceAreaContentService';

const AreaContentManagement = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    serviceArea: 'Zonnepanelen',
    contentType: 'general',
    displayOrder: 0,
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [filterServiceArea, setFilterServiceArea] = useState('all');

  const serviceAreas = [
    'Zonnepanelen',
    'Elektrische Installatie',
    'HVAC Systemen',
    'Loodgieters',
    'Kraan Operators',
    'Stratenmakers & Grondwerkers'
  ];

  const contentTypes = [
    { value: 'general', label: 'Algemeen' },
    { value: 'benefits', label: 'Voordelen' },
    { value: 'expertise', label: 'Expertise' },
    { value: 'safety', label: 'Veiligheid' },
    { value: 'quality', label: 'Kwaliteit' },
    { value: 'services', label: 'Diensten' },
    { value: 'efficiency', label: 'Efficiëntie' },
    { value: 'comfort', label: 'Comfort' },
    { value: 'experience', label: 'Ervaring' },
    { value: 'craftsmanship', label: 'Vakmanschap' },
    { value: 'sustainability', label: 'Duurzaamheid' }
  ];

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await serviceAreaContentService?.getAllContent();
      setContent(data || []);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (contentItem = null) => {
    if (contentItem) {
      setEditingContent(contentItem);
      setFormData({
        title: contentItem?.title,
        content: contentItem?.content,
        serviceArea: contentItem?.service_area,
        contentType: contentItem?.content_type,
        displayOrder: contentItem?.display_order,
        isActive: contentItem?.is_active
      });
    } else {
      setEditingContent(null);
      setFormData({
        title: '',
        content: '',
        serviceArea: 'Zonnepanelen',
        contentType: 'general',
        displayOrder: 0,
        isActive: true
      });
    }
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingContent(null);
    setFormData({
      title: '',
      content: '',
      serviceArea: 'Zonnepanelen',
      contentType: 'general',
      displayOrder: 0,
      isActive: true
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.title?.trim()) {
      newErrors.title = 'Titel is verplicht';
    }
    if (!formData?.content?.trim()) {
      newErrors.content = 'Inhoud is verplicht';
    }
    if (!formData?.serviceArea) {
      newErrors.serviceArea = 'Vakgebied is verplicht';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingContent) {
        await serviceAreaContentService?.updateContent(editingContent?.id, formData);
      } else {
        await serviceAreaContentService?.createContent(formData);
      }
      await loadContent();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving content:', error);
      setErrors({ submit: error?.message });
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Weet je zeker dat je deze inhoud wilt verwijderen?')) return;

    try {
      await serviceAreaContentService?.deleteContent(contentId);
      await loadContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Fout bij verwijderen: ' + error?.message);
    }
  };

  const filteredContent = filterServiceArea === 'all' 
    ? content 
    : content?.filter(c => c?.service_area === filterServiceArea);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Vakgebied Inhoud Beheer</h2>
          <p className="text-sm text-muted-foreground">Beheer vakgebied-specifieke inhoud</p>
        </div>
        <Button onClick={() => handleOpenModal()} iconName="Plus">
          Inhoud Toevoegen
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm font-medium text-foreground">Filter op vakgebied:</label>
        <select
          value={filterServiceArea}
          onChange={(e) => setFilterServiceArea(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">Alle vakgebieden</option>
          {serviceAreas?.map(area => (
            <option key={area} value={area}>{area}</option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">
          ({filteredContent?.length} items)
        </span>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Laden...</div>
      ) : (
        <div 
          className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
          style={{
            boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Titel</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Vakgebied</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Volgorde</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredContent?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                      Geen inhoud gevonden
                    </td>
                  </tr>
                ) : (
                  filteredContent?.map((contentItem) => (
                    <tr key={contentItem?.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm text-foreground font-medium">{contentItem?.title}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{contentItem?.service_area}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {contentTypes?.find(t => t?.value === contentItem?.content_type)?.label || contentItem?.content_type}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{contentItem?.display_order}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          contentItem?.is_active 
                            ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                        }`}>
                          {contentItem?.is_active ? 'Actief' : 'Inactief'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Edit"
                            onClick={() => handleOpenModal(contentItem)}
                          >
                            Bewerken
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleDelete(contentItem?.id)}
                          >
                            Verwijderen
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-foreground">
                  {editingContent ? 'Inhoud Bewerken' : 'Inhoud Toevoegen'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Titel *
                  </label>
                  <Input
                    value={formData?.title}
                    onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                    placeholder="Titel van de inhoud"
                  />
                  {errors?.title && (
                    <p className="text-sm text-error mt-1">{errors?.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Inhoud *
                  </label>
                  <textarea
                    value={formData?.content}
                    onChange={(e) => setFormData({ ...formData, content: e?.target?.value })}
                    placeholder="Inhoud tekst"
                    rows={6}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errors?.content && (
                    <p className="text-sm text-error mt-1">{errors?.content}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Vakgebied *
                  </label>
                  <select
                    value={formData?.serviceArea}
                    onChange={(e) => setFormData({ ...formData, serviceArea: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {serviceAreas?.map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                  {errors?.serviceArea && (
                    <p className="text-sm text-error mt-1">{errors?.serviceArea}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Type
                  </label>
                  <select
                    value={formData?.contentType}
                    onChange={(e) => setFormData({ ...formData, contentType: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {contentTypes?.map(type => (
                      <option key={type?.value} value={type?.value}>{type?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Weergave Volgorde
                  </label>
                  <Input
                    type="number"
                    value={formData?.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e?.target?.value) || 0 })}
                    placeholder="0"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData?.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <label className="text-sm text-foreground">Actief</label>
                </div>

                {errors?.submit && (
                  <p className="text-sm text-error">{errors?.submit}</p>
                )}

                <div className="flex items-center gap-3 pt-4">
                  <Button type="submit" variant="default">
                    {editingContent ? 'Bijwerken' : 'Toevoegen'}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCloseModal}>
                    Annuleren
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaContentManagement;